import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { waitForBaleInitData } from '../services/bale';
import '../styles/Login.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, updateUser } = useAuth();

  const isSettingPassword = useRef(false);
  const hasAttemptedBaleLogin = useRef(false);

  const [loginMethod, setLoginMethod] = useState('username');
  const [step, setStep] = useState('input');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtpCode, setDevOtpCode] = useState('');
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost/backend';

  // 🟢 Helper function برای redirect
  const getRedirectPath = () => {
    return location.state?.returnTo || '/dashboard';
  };

  useEffect(() => {
    let isActive = true;

    if (isAuthenticated || hasAttemptedBaleLogin.current) return undefined;

    const authenticateWithBale = async () => {
      const initData = await waitForBaleInitData();
      if (!isActive || !initData || hasAttemptedBaleLogin.current) return;

      hasAttemptedBaleLogin.current = true;
      setLoginMethod('phone');
    };

    authenticateWithBale();

    return () => {
      isActive = false;
    };
  }, [apiUrl, isAuthenticated, login, navigate, location.state]);

  // اگه لاگین کرده، برو داشبورد (اما نه وقتی داره پسورد ست می‌کنه)
  useEffect(() => {
    if (isAuthenticated && !isSettingPassword.current) {
      navigate(getRedirectPath());
    }
  }, [isAuthenticated, navigate]);

  // لاگین با یوزرنیم و پسورد
  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('یوزرنیم و رمز عبور الزامی است');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/api/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        login(data.token, data.user);
        navigate(getRedirectPath()); // 🟢
      } else {
        setError(data.error || 'یوزرنیم یا رمز عبور اشتباه است');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // ارسال OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!phone.match(/^09[0-9]{9}$/)) {
      setError('شماره موبایل نامعتبر است');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/api/auth/send-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (data.success) {
        setStep('otp');
        // TODO: Remove temporary OTP display after connecting the SMS provider.
        setDevOtpCode(data.dev_code || '');
      } else {
        setError(data.error || 'خطا در ارسال کد');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // تأیید OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('کد باید 6 رقم باشد');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/api/auth/verify-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: otp }),
      });

      const data = await response.json();

      if (data.success && data.requires_registration) {
        login(data.token, data.user);
        isSettingPassword.current = true;
        setStep('register');
      } else if (data.success) {
        const hasPassword = data.user.has_set_password;
        const hasUsername = data.user.username;
        
        login(data.token, data.user);
        
        if (!hasPassword && !hasUsername) {
          isSettingPassword.current = true;
          setStep('set-password');
        } else if (!hasPassword && hasUsername) {
          isSettingPassword.current = true;
          setStep('set-password');
        } else {
          navigate(getRedirectPath()); // 🟢
        }
      } else {
        setError(data.error || 'کد نامعتبر است');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // ثبت‌نام
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('نام الزامی است');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/user/profile.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          age: age ? parseInt(age) : null,
          gender: gender || null,
        }),
      });

      const data = await response.json();

      if (data.success) {
        updateUser({ ...user, name: name.trim(), age: age ? parseInt(age) : null, gender: gender || null });
        isSettingPassword.current = true;
        setStep('set-password');
      } else {
        setError(data.error || 'خطا در ثبت‌نام');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // تنظیم رمز عبور
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!newUsername.match(/^[a-zA-Z0-9_]{3,20}$/)) {
      setError('یوزرنیم باید بین 3 تا 20 کاراکتر و فقط شامل حروف، اعداد و _ باشد');
      return;
    }

    if (newPassword.length < 6) {
      setError('رمز عبور باید حداقل 6 کاراکتر باشد');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('رمز عبور و تکرار آن یکسان نیستند');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/auth/set-password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        isSettingPassword.current = false;
        alert('✅ یوزرنیم و رمز عبور با موفقیت تنظیم شد!');
        navigate(getRedirectPath()); // 🟢
      } else {
        setError(data.error || 'خطا در تنظیم رمز عبور');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPassword = () => {
    isSettingPassword.current = false;
    navigate(getRedirectPath()); // 🟢
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🔐 ورود به حساب</h1>
          <p>برای مدیریت تست‌هات وارد شو</p>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        )}

        {step === 'input' && (
          <>
            <div className="login-method-tabs">
              <button
                className={`tab ${loginMethod === 'username' ? 'active' : ''}`}
                onClick={() => setLoginMethod('username')}
              >
                یوزرنیم و رمز عبور
              </button>
              <button
                className={`tab ${loginMethod === 'phone' ? 'active' : ''}`}
                onClick={() => setLoginMethod('phone')}
              >
                ورود با موبایل
              </button>
            </div>

            {loginMethod === 'username' && (
              <form onSubmit={handleUsernameLogin} className="login-form">
                <div className="form-group">
                  <label>یوزرنیم</label>
                  <input
                    type="text"
                    placeholder="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>رمز عبور</label>
                  <input
                    type="password"
                    placeholder="••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'در حال ورود...' : 'ورود'}
                </button>

                <button
                  type="button"
                  className="btn-link"
                  onClick={() => navigate('/forgot-password')}
                >
                  فراموشی رمز عبور
                </button>
              </form>
            )}

            {loginMethod === 'phone' && (
              <form onSubmit={handleSendOTP} className="login-form">
                <div className="form-group">
                  <label>شماره موبایل</label>
                  <input
                    type="tel"
                    placeholder="09123456789"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={11}
                    disabled={loading}
                    autoFocus
                  />
                  <small>کد تأیید برات پیامک میشه</small>
                </div>

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'در حال ارسال...' : 'دریافت کد تأیید'}
                </button>
              </form>
            )}
          </>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} className="login-form">
            <div className="form-group">
              <label>کد تأیید</label>
              <input
                type="text"
                placeholder="کد 6 رقمی"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                disabled={loading}
                autoFocus
              />
              <small>کد به {phone} ارسال شد</small>
              {devOtpCode && <p className="dev-otp-code">کد تست: {devOtpCode}</p>}
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'در حال تأیید...' : 'تأیید و ورود'}
            </button>

            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep('input')}
              disabled={loading}
            >
              تغییر شماره
            </button>
          </form>
        )}

        {step === 'register' && (
          <form onSubmit={handleRegister} className="login-form">
            <p className="welcome-text">خوش اومدی! 🎉</p>
            <p className="subtitle">لطفاً اطلاعاتت رو تکمیل کن</p>

            <div className="form-group">
              <label>نام *</label>
              <input
                type="text"
                placeholder="نام خود را وارد کنید"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>سن (اختیاری)</label>
              <input
                type="number"
                placeholder="سن"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                min="1"
                max="120"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>جنسیت (اختیاری)</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} disabled={loading}>
                <option value="">انتخاب کنید</option>
                <option value="male">مرد</option>
                <option value="female">زن</option>
                <option value="other">ترجیح میدم نگم</option>
              </select>
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'در حال ثبت‌نام...' : 'ادامه'}
            </button>
          </form>
        )}

        {step === 'set-password' && (
          <form onSubmit={handleSetPassword} className="login-form">
            <p className="welcome-text">تنظیم یوزرنیم و رمز عبور 🔑</p>
            <p className="subtitle">
              برای دفعات بعد می‌تونی با یوزرنیم و رمز عبور وارد شی
              <br />
              <small style={{ color: '#94a3b8' }}>(می‌تونی این مرحله رو رد کنی)</small>
            </p>

            <div className="form-group">
              <label>یوزرنیم *</label>
              <input
                type="text"
                placeholder="username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                disabled={loading}
                autoFocus
              />
              <small>فقط حروف انگلیسی، اعداد و _ (3-20 کاراکتر)</small>
            </div>

            <div className="form-group">
              <label>رمز عبور *</label>
              <input
                type="password"
                placeholder="حداقل 6 کاراکتر"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>تکرار رمز عبور *</label>
              <input
                type="password"
                placeholder="تکرار رمز عبور"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'در حال تنظیم...' : 'تنظیم و ورود'}
            </button>

            <button
              type="button"
              className="btn-secondary"
              onClick={handleSkipPassword}
              disabled={loading}
            >
              بعداً تنظیم می‌کنم
            </button>
          </form>
        )}

        <div className="login-footer">
          <button className="btn-link" onClick={() => navigate('/')}>
            ← بازگشت به صفحه اصلی
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
